/* eslint-disable */
import { useState, useEffect, useMemo } from "react";
import { OLAPTransformerResult, useParameter, useDictionary, Column } from "../../../shared";
import Highcharts, { SeriesOptionsType } from "highcharts";
import { useChartOptions } from "../../../../charts/useChartOptions";
import { legendPipe, chartPipe, tooltipPipe, addXAxisPipe, addYAxisPipe } from "../../../../charts/pipes";
import { dictionaryService } from "../../../dataFlow";
import { arrayByLength, uniq } from "../../../../../shared";
import { last, orderBy, uniqBy } from "lodash";

const CATEGORIES_AXIS = "calendar.default";
const SERIES_AXIS = "commodities.default";
const [YEAR_FREQUENCY, MONTH_FREQUENCY, WEEK_FREQUENCY] = [1, 4, 7];

export function useHighchartTransformer(olapDataCommodities: OLAPTransformerResult | null, olapDataIndex: OLAPTransformerResult | null): Highcharts.Options | undefined {
  const [state, setState] = useState<Highcharts.Options>();
  const [calendarParameter] = useParameter<number[]>("@calendar");
  const [calendarItems] = useDictionary(dictionaryService, "calendar-origin");

  const olapColumns = useMemo(() => {
    const allColumns = [olapDataCommodities, olapDataIndex].filter(Boolean).flatMap(e => e?.columns);
    const uniqColumns = uniqBy(allColumns, e => e?.selection[CATEGORIES_AXIS]).filter(Boolean);
    const primarySortedColumns = orderBy(uniqColumns, e => e?.selection[CATEGORIES_AXIS]);
    const secondarySortedColumns = orderBy(primarySortedColumns, e => String(e?.selection[CATEGORIES_AXIS]).slice(1, 5));
    return secondarySortedColumns as Column[];
  }, [olapDataCommodities, olapDataIndex]);

  const [monthesCategories] = useMemo(() =>
    [MONTH_FREQUENCY]
      .map(frequency => {
        const frequencyColumns = olapColumns.filter(colum => {
          const colFrequency = +String(colum.selection[CATEGORIES_AXIS])[0];
          return colFrequency === frequency;
        });
        const frequencyCalendarIds = calendarParameter?.filter(calendarId => {
          const calendarFrequency = Number(String(calendarId)[0]);
          return calendarFrequency === frequency;
        });
        const lastColumn = frequencyColumns[frequencyColumns.length - 1];
        const lastColIndex = frequencyCalendarIds?.indexOf(lastColumn?.selection[CATEGORIES_AXIS] as number) || 0;
        const ignoreCalendarIds = frequencyCalendarIds?.slice(lastColIndex + 1) || [];

        return frequencyCalendarIds?.filter(calendarId => !ignoreCalendarIds.includes(calendarId)).map(calendarId => {
          const calendarItem = calendarItems?.find(item => item.id === String(calendarId));
          const name = calendarItem?.getValue<string>("name") || '';
          return {
            calendarId, name
          };
        }) || []
      })
    , [olapColumns, calendarItems, calendarParameter]
  );

  const options = useChartOptions({
    tooltip: {
      useHTML: true,
      formatter: function () {
        const { category, unit } = this.point as any;
        const pointValue = Highcharts.numberFormat(this.point.y || 0, 0);
        return `<div>
            <b>${this.point.series.name}</b><br/>
            <b>${category}</b>: <span>${pointValue} ${unit}</span>
        </div>`;
      },
      outside: true,
      style: {
        zIndex: 100,
      },
    },
  }, [
    chartPipe("spline"),
    legendPipe("bottom", {
      useHTML: true,
      labelFormatter: function () {
        const { name } = this;
        return name;
      }
    }),
    addXAxisPipe(monthesCategories.map(category => category.name), {
      visible: true,
      labels: {
        enabled: true,
        formatter: function () {
          const label = this.axis.defaultLabelFormatter.call(this);
          return label;
        }
      },

    }),
    addYAxisPipe({
      gridLineColor: '#fff',
      title: {
        text: "",
      },
      labels: {
        enabled: true,
        formatter: function () {
          const label = this.axis.defaultLabelFormatter.call(this);
          if (/^[0-9]{4}$/.test(label)) {
            return Highcharts.numberFormat(this.value, 0);
          }
          return label;
        },
      },
    }),
  ]);

  useEffect(() => {
    if (olapDataCommodities && olapDataIndex) {
      const colors = ['#1CA955', '#E38114', '#DD5C3D', '#8F8F8F', '#D9D10C', '#2D5EDA', '#784FBA', '#D656A3'];
      const commoditiesSeries: SeriesOptionsType[] = olapDataCommodities.rows.map((row, rowIndex) => {
        let commodityName = row.attributes[`${SERIES_AXIS}.name`];

        if (typeof commodityName === 'object') {
          commodityName = commodityName[9] || [];
        }
        return {
          name: commodityName,
          type: 'spline',
          data: monthesCategories?.map((category, categoryIndex) => {
            const point = row.points.find(p => p.column.selection[CATEGORIES_AXIS] === category.calendarId);
            const value = point?.value || null;
            const isLast = categoryIndex === monthesCategories.length - 1;
            return { x: categoryIndex, y: value, date: category.name, isLast, unit: '%' };
          }) || [],
          yAxis: 0,
          xAxis: 0,
          color: colors[rowIndex],
          zIndex: 2,
          marker: {
            radius: 0,
          },
          showInLegend: true,
          states: {
            hover: {
              lineWidth: 0,
              lineWidthPlus: 0,
            },
          },
          dataLabels: {
            enabled: true,
            useHTML: true,
            allowOverlap: true,
            y: -4,
            formatter: function () {
              const isMax = this.point['isMax'];
              return isMax ? `${Highcharts.numberFormat(this.point.y!, 0)} <span style='font-size: 10px'>(max)</span>` : '';
            }
          }
        }
      });

      const indexSerie: SeriesOptionsType = {
        name: 'Index',
        type: 'spline',
        data: monthesCategories?.map((category, categoryIndex) => {
          const row = olapDataIndex.rows[0];
          const point = row.points.find(p => p.column.selection[CATEGORIES_AXIS] === category.calendarId);
          const value = point?.value || null;
          const isLast = categoryIndex === monthesCategories.length - 1;
          return { x: categoryIndex, y: value, date: category.name, isLast, unit: '%' };
        }) || [],
        yAxis: 0,
        xAxis: 0,
        color: '#0F8AC0',
        zIndex: 2,
        marker: {
          radius: 0,
        },
        showInLegend: true,
        states: {
          hover: {
            lineWidth: 0,
            lineWidthPlus: 0,
          },
        },
        dataLabels: {
          enabled: true,
          useHTML: true,
          allowOverlap: true,
          inside: false,
          x: -10,
          y: -20,
          formatter: function () {
            const isLast = this.point['isLast'];
            const value = (this.point.y || 0) - 100;
            const dynamic = value > 0 ? '+' : '';
            const display = `ACT ${dynamic}${Highcharts.numberFormat(value, 0)}`;
            return isLast ? `<span style='color: #0F8AC0;font-size: 12px'>${display}%</span>` : '';
          }
        }
      }

      const series: SeriesOptionsType[] = [...commoditiesSeries, indexSerie];
      const result: Highcharts.Options = {
        ...options,
        series,
      };
      setState(result);
    } else {
      setState(undefined);
    }
  }, [olapDataCommodities, olapDataIndex, olapColumns, monthesCategories]);
  return state;
}
